/**
 * B9 — Pure data for the sidebar navigation. Lifted out of AppShell.tsx
 * so it's testable without mounting React. AppShell imports from here;
 * the regression test imports the label-only helper below.
 *
 * The exact matrix asserted by getNavItems.test.ts:
 *
 *   staff      Home · Clock In · People · Time Off · Performance & Pay · Training          (6)
 *   manager    Home · Clock In · People · Time Off · Hiring · Performance & Pay · Training (7)
 *   hr_admin   ...as manager, plus Admin                                                   (8)
 *   it_admin   ...as manager, plus Admin and Audit Logs                                    (9)
 *   super_admin Tenants · User Management · Module Permissions · Audit Logs                 (4)
 *
 * Editing the tree? Update the test matrix in getNavItems.test.ts too.
 */

import {
  Home, Clock, Calendar, Users, Briefcase, Trophy, Settings, GraduationCap,
  FileText, Building2, ShieldCheck,
} from 'lucide-react';
import type { UserRole } from '../../types/user.types';

export interface NavItem {
  label: string;
  path: string;
  icon: typeof Home;
  moduleKey?: string;
}

export function getNavItems(role: UserRole, _userId: string, isRecruiter = false): NavItem[] {
  if (role === 'super_admin') {
    return [
      { label: 'Tenants', path: '/admin/tenants', icon: Building2 },
      { label: 'User Management', path: '/admin/user-management', icon: Users },
      { label: 'Module Permissions', path: '/admin/permissions', icon: ShieldCheck },
      { label: 'Audit Logs', path: '/admin/audit-logs', icon: FileText },
    ];
  }

  const items: NavItem[] = [];

  // 1. Home — all roles
  items.push({ label: 'Home', path: '/dashboard', icon: Home, moduleKey: 'dashboard' });

  // 2. Clock In — EVERY role. HR and IT admins are employees with shifts too, and
  //    used to have no way in at all: the page was gated to staff+manager and never
  //    appeared in any nav for them.
  //
  //    B9 originally kept this out of the sidebar as "a workflow page, not a nav
  //    destination", but the first-run tour has always said otherwise — it tells
  //    users to "jump between Home, Clock In, Time Off" and then points at
  //    #tour-nav-clock-in, a sidebar anchor that never rendered. The sidebar is also
  //    desktop-only, so without this entry a desktop admin has no route to the page.
  //
  //    Placed second because it is the most frequent daily action in the product.
  //    moduleKey wires up the 'clock_in' entry that already existed in the backend's
  //    MODULES registry with nothing consuming it.
  items.push({ label: 'Clock In', path: '/clock-in', icon: Clock, moduleKey: 'clock_in' });

  // 3. People — EVERY role, including staff.
  //    Staff gets directory-only access (read the list, click into profiles
  //    for basic contact info). Staff does NOT see the Offboarding /
  //    Onboarding / Team Reports sub-tabs; staff does NOT see edit actions
  //    on anyone else's profile. That gating happens in People.tsx via
  //    the viewPermission hint + in peopleTabs() via role filtering, then
  //    through useVisibleTabs() for the admin module-permissions matrix.
  items.push({ label: 'People', path: '/people', icon: Users, moduleKey: 'people' });

  // 4. Time Off — all roles (IT admin sees it even though they primarily
  //    live in Admin; per the approved matrix Time Off is universal).
  items.push({ label: 'Time Off', path: '/time-off', icon: Calendar, moduleKey: 'time_off' });

  // 5. Hiring — managers, admins, and staff with the is_recruiter flag.
  if (role === 'manager' || role === 'hr_admin' || role === 'it_admin' || isRecruiter) {
    items.push({ label: 'Hiring', path: '/hiring', icon: Briefcase, moduleKey: 'hiring' });
  }

  // 6. Performance & Pay — everyone (staff sees Performance sub-tab only
  //    via hubTabs filtering; Compensation is gated inside the hub).
  items.push({ label: 'Performance & Pay', path: '/performance-pay', icon: Trophy, moduleKey: 'performance' });

  // 6b. Training — all non-super_admin roles.
  items.push({ label: 'Training', path: '/training', icon: GraduationCap });

  // 7. Admin — HR + IT admins only. Audit Logs is a separate direct tab for
  //    IT admins; HR admins must not see or access it.
  if (role === 'hr_admin' || role === 'it_admin') {
    items.push({ label: 'Admin', path: '/admin', icon: Settings, moduleKey: 'reports' });
  }
  if (role === 'it_admin') {
    items.push({ label: 'Audit Logs', path: '/admin/audit-logs', icon: FileText });
  }

  return items;
}

/**
 * Label-only wrapper for the visibility-matrix regression test. Keeps the
 * test free of any React / lucide imports so it runs under pure
 * `node --test` with no extra tooling.
 */
export function getSidebarLabelsForRole(role: UserRole, isRecruiter = false): string[] {
  return getNavItems(role, 'test-user-id', isRecruiter).map((item) => item.label);
}
